import { Link } from "@tanstack/react-router";
import { UNASSIGNED } from "@/components/profitability/clientPartner";
import { DrillInDialog } from "@/components/profitability/DrillInDialog";
import { MarginCell } from "@/components/profitability/MarginCell";
import { useAccounts } from "@/hooks/use-accounts";
import { money } from "@/lib/format";
import type { ClientPartnerRow } from "@/lib/profitability-api";

const BOOK_LIMIT = 200;

/**
 * The accounts behind one Client Partner's row.
 *
 * Fetched through the SAME server-side filter the accounts grid uses, rather
 * than from anything this page holds: the row knows the partner's totals but
 * not which accounts produced them, and re-deriving a list here would be a
 * second answer to a question the accounts endpoint already answers.
 *
 * The Unassigned row drills in too -- `unassigned` is a value the endpoint
 * takes, and "which accounts does nobody own" is the most actionable question
 * on the page.
 */
export function ClientPartnerBookDialog({
  partner,
  range,
  onOpenChange,
}: {
  partner: ClientPartnerRow | null;
  range?: string;
  onOpenChange: (open: boolean) => void;
}) {
  const selection = partner
    ? [partner.is_unassigned ? UNASSIGNED : partner.person_name]
    : undefined;
  const { data, isLoading, isError, error } = useAccounts(
    { search: "", status: "", sort: "revenue", sortDir: "desc" },
    { cursor: null, direction: "next" },
    range,
    undefined,
    partner ? selection : undefined,
    // Enough to hold the largest book (65 today) in one page. This dialog does
    // not page, so the default 50 would silently show part of a partner's book
    // while the row beside it still said 65.
    BOOK_LIMIT,
  );

  const rows = partner ? (data?.rows ?? []) : [];
  const shown = rows.length;

  return (
    <DrillInDialog
      open={partner !== null}
      onOpenChange={onOpenChange}
      title={partner ? `${partner.person_name} · ${partner.accounts} accounts` : ""}
      description={
        partner
          ? `${money(partner.revenue)} revenue, ${money(partner.profit)} profit, ${
              partner.margin_pct === null ? "no margin" : `${partner.margin_pct}% margin`
            }${
              partner.gap_vs_benchmark && partner.gap_vs_benchmark > 0
                ? ` · ${money(partner.gap_vs_benchmark)} below benchmark`
                : ""
            }`
          : undefined
      }
    >
      {isError ? (
        <p className="text-sm text-destructive">
          {(error as Error)?.message ?? "Could not load these accounts"}
        </p>
      ) : isLoading ? (
        <p className="text-sm text-muted-foreground">Loading accounts...</p>
      ) : (
        <>
          {/* The row's own count against what came back. They agree today; if
              they ever disagree the page says so rather than quietly showing a
              shorter list than the number beside the name. */}
          {partner && shown !== partner.accounts ? (
            <p className="mb-2 text-xs text-muted-foreground">
              Showing {shown} of the {partner.accounts} accounts counted in this row.
            </p>
          ) : null}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="py-2 pr-4 font-medium">Account</th>
                <th className="py-2 pr-4 text-right font-medium">Revenue</th>
                <th className="py-2 pr-4 text-right font-medium">Cost</th>
                <th className="py-2 text-right font-medium">Margin</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.account_id} className="border-b border-border last:border-0">
                  <td className="py-2 pr-4">
                    <Link
                      to="/dashboard/accounts/$accountId"
                      params={{ accountId: row.account_id }}
                      search={{ range }}
                      className="hover:underline"
                      onClick={() => onOpenChange(false)}
                    >
                      {row.account_name ?? row.account_id}
                    </Link>
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums">{money(row.revenue)}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{money(row.cost)}</td>
                  <td className="py-2 text-right">
                    <MarginCell value={row.margin_pct} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </DrillInDialog>
  );
}
